import { defineStore } from 'pinia'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import LinkedServiceAPI from '@/modules/linkedservice/constants/linkedservice.api.uri.const.ts'
import { formatDataToSubmit } from '../helper/linkedservice'

export enum LinkedServiceDetailActionType {
  FETCHING = 'fetching',
  FETCHED = 'fetched',
  FETCH_FAILED = 'fetch_failed',
  UPDATING = 'updating',
  UPDATED = 'updated',
  UPDATE_FAILED = 'update_failed',
}

interface LinkedServiceDetailState {
  linkedServices: {
    [key: string]: {
      action_type: LinkedServiceDetailActionType
      action_message: string
      data: any
      formData: any
    }
  }
}

export const useLinkedServiceDetailStore = defineStore('linkedService.detail', {
  state: (): LinkedServiceDetailState => ({
    linkedServices: {},
  }),
  getters: {},
  actions: {
    async removeLinkedServiceDetail(id: number) {
      return new Promise(async (resolve, reject) => {
        try {
          if (!this.linkedServices[id]) {
            throw new Error('linked service not found')
          }
          delete this.linkedServices[id]
          resolve(true)
        } catch (e) {
          console.error('Error removing linked service detail:', e)
          reject(e)
        }
      })
    },
    async removeAllLinkedServiceDetail() {
      return new Promise(async (resolve, reject) => {
        try {
          this.linkedServices = {}
          resolve(true)
        } catch (e) {
          console.error('Error removing all linked service details:', e)
          reject(e)
        }
      })
    },
    async fetchLinkedServiceDetail(id: number) {
      const existingService = this.linkedServices[id] && this.linkedServices[id].data
      return new Promise(async (resolve, reject) => {
        try {
          // fetching
          this.linkedServices[id] = {
            ...this.linkedServices[id],
            action_type: existingService ? this.linkedServices[id].action_type : LinkedServiceDetailActionType.FETCHING,
          }
          const res = await APIClient.get(LinkedServiceAPI.detail.replace(':id', id.toString()))
          this.linkedServices[id] = {
            action_type: LinkedServiceDetailActionType.FETCHED,
            action_message: res?.data?.message || '',
            data: res.data.data,
            formData: {},
          }
          resolve(this.linkedServices[id].data)
        } catch (e) {
          this.linkedServices[id].action_type = LinkedServiceDetailActionType.FETCH_FAILED
          this.linkedServices[id].action_message = e.message || 'Failed to fetch LinkedService detail'
          reject(e)
        }
      })
    },
    async setLinkedServiceDetail(id: number, account: Object) {
      this.linkedServices[id] = {
        action_type: LinkedServiceDetailActionType.FETCHED,
        action_message: '',
        account: { ...this.linkedServices[id].account, ...account },
      }
    },
    async updateLinkedServiceDetail(id: number, data: any) {
      return new Promise(async (resolve, reject) => {
        try {
          if (!this.linkedServices[id]) {
            throw new Error('linked service not found')
          }
          if (this.linkedServices[id].action_type === LinkedServiceDetailActionType.UPDATING) {
            throw new Error('linked service is already being updating')
          }
          this.linkedServices[id] = {
            ...this.linkedServices[id],
            action_type: LinkedServiceDetailActionType.UPDATING,
          }
          const submitValues = formatDataToSubmit({
            ...data,
          })
          const res = await APIClient.put(
            LinkedServiceAPI.update.replace(':id', id.toString()),
            submitValues,
          )
          this.linkedServices[id] = {
            ...this.linkedServices[id],
            action_type: LinkedServiceDetailActionType.UPDATED,
            action_message: res?.data?.message || '',
            data: res?.data?.data,
            formData: {},
          }
          resolve(this.linkedServices[id].data)
        } catch (e) {
          console.error('Error update linked service detail:', e)
          this.linkedServices[id].action_type = LinkedServiceDetailActionType.UPDATE_FAILED
          this.linkedServices[id].action_message = e.message || 'Failed to update linked service detail'
          reject(e)
        }
      })
    },
  },
})
